import React, { useCallback, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useStore, StoreTypes } from 'context';
import { PainterMode } from 'constants/painterModes';
import { ReaderToolType } from 'constants/ReaderTools';
import { useCanvasViewTransform } from 'customHooks/canvasViewTransform';
import { drawLaserStroke, strokeAlpha, isStrokeExpired } from './draw';
import styles from './index.module.scss';

/**
 * 雷射筆繪製層（NBK-1676）
 *
 * 這是一張疊在 fabric 主畫布之上的獨立 2D canvas。雷射筆啟用時它吃下所有
 * pointer 事件，fabric 的 .upper-canvas 從頭到尾收不到任何一個，
 * 因此 fabric 的 mouse:up → CanvasFinishPaintingEvent → saveCanvasJSON
 * 這條存檔鏈不會被觸發 —— 不是攔截，是沒觸發。
 *
 * 軌跡只以純 JS 陣列存在 strokesRef 裡，從來不是 fabric object，
 * 所以 canvas.toJSON()、undo 堆疊、convertJSONToSVG 都撈不到它。
 */

const LaserCanvas = ({ className }) => {
  const [
    {
      fullWidthInfo,
      scale,
      offsetX,
      offsetY,
      pageIndex,
      readerToolType,
      objectPointerEventStatus
    }
  ] = useStore(StoreTypes.reader);
  const [{ style, bookId }] = useStore(StoreTypes.books);
  const [{ painterMode, laserPenTool }] = useStore(StoreTypes.canvas);

  const { width, height } = style;

  // 同時檢查 painterMode 與「目前工具」：像滑鼠（Drag）這類工具只改 readerToolType、
  // 不走 changePainterMode，單看 painterMode 會讓 overlay 留在最上層把整頁點不動。
  // 兩者皆須成立，任一條件不符就關閉 overlay —— 失敗方向永遠是「放行」而非「卡住」。
  const isLaserMode =
    painterMode === PainterMode.LaserPen &&
    readerToolType === ReaderToolType.LaserPen;

  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  // 尚未淡出完畢的所有軌跡。刻意用 ref：這是每幀更新的高頻資料，
  // 放進 state 會在畫寫過程中不斷觸發 re-render。
  const strokesRef = useRef([]);
  const activeStrokeRef = useRef(null);
  const rafRef = useRef(null);

  // 供原生事件處理器讀取最新值，避免因為值變動而反覆解除／重掛監聽
  const laserPenToolRef = useRef(laserPenTool);
  laserPenToolRef.current = laserPenTool;
  const isLaserModeRef = useRef(isLaserMode);
  isLaserModeRef.current = isLaserMode;

  const viewTransform = useCanvasViewTransform({
    width,
    height,
    scale,
    offsetX,
    offsetY,
    fullWidthScale: fullWidthInfo.scale
  });

  useEffect(() => {
    if (!wrapperRef.current) return;
    wrapperRef.current.style.transform = viewTransform;
  }, [viewTransform, isLaserMode]);

  const renderFrame = useCallback(() => {
    rafRef.current = null;

    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    const ctx = canvasEl.getContext('2d');
    const now = performance.now();

    // 淡出完成的直接移除；下一次 clearRect 之後畫布就是全透明，不留下殘影、白線或彩色邊框。
    strokesRef.current = strokesRef.current.filter(
      stroke => !isStrokeExpired(stroke, now)
    );

    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);

    for (const stroke of strokesRef.current) {
      // 每筆依自己的放開時間獨立計算透明度
      ctx.globalAlpha = strokeAlpha(stroke, now);
      drawLaserStroke(ctx, stroke);
    }
    ctx.globalAlpha = 1;

    // 沒有軌跡就讓迴圈自然停下，閒置時完全不佔用資源
    if (strokesRef.current.length > 0) {
      rafRef.current = requestAnimationFrame(renderFrame);
    }
  }, []);

  const scheduleRender = useCallback(() => {
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(renderFrame);
    }
  }, [renderFrame]);

  const clearAll = useCallback(() => {
    strokesRef.current = [];
    activeStrokeRef.current = null;

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const canvasEl = canvasRef.current;
    if (canvasEl) {
      canvasEl
        .getContext('2d')
        .clearRect(0, 0, canvasEl.width, canvasEl.height);
    }
  }, []);

  // 切換工具、換頁、換書：未消失的軌跡立即清除
  useEffect(() => {
    if (!isLaserMode) clearAll();
  }, [isLaserMode, clearAll]);

  useEffect(() => {
    clearAll();
  }, [pageIndex, bookId, clearAll]);

  // 卸載時停掉 rAF，避免留下孤兒迴圈
  useEffect(() => () => clearAll(), [clearAll]);

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    /**
     * 事件座標換算。getBoundingClientRect 回傳的是最終螢幕座標，
     * 已經包含 wrapper 的放大補償、BookContainer 的 scaleX 與 PinchPan 的位移，
     * 所以這一行同時解決一般頁面、區間放大、四格放大三種狀態，不必各別處理。
     */
    const toCanvasPoint = event => {
      const rect = canvasEl.getBoundingClientRect();
      return {
        x: (event.clientX - rect.left) * (canvasEl.width / rect.width),
        y: (event.clientY - rect.top) * (canvasEl.height / rect.height)
      };
    };

    const handlePointerDown = event => {
      if (!isLaserModeRef.current) return;
      // 只有主要按鍵才起筆：滑鼠左鍵 button === 0，觸控與筆尖同樣是 0
      // 比照 fabric 的 fireRightClick / fireMiddleClick 預設 false
      if (event.button !== 0) return;
      if (activeStrokeRef.current) return;
      event.preventDefault();

      try {
        canvasEl.setPointerCapture(event.pointerId);
      } catch (e) {
        /* 某些裝置在多點觸控時會拒絕，忽略即可 */
      }

      const { colorHex, lineWidth } = laserPenToolRef.current;
      // 顏色粗細在起筆當下複製進軌跡，之後改設定不會回頭影響已畫出的筆畫
      activeStrokeRef.current = {
        pointerId: event.pointerId,
        points: [toCanvasPoint(event)],
        color: colorHex,
        width: lineWidth,
        releasedAt: null
      };
      strokesRef.current.push(activeStrokeRef.current);
      scheduleRender();
    };

    const handlePointerMove = event => {
      const stroke = activeStrokeRef.current;
      if (!stroke || stroke.pointerId !== event.pointerId) return;
      event.preventDefault();

      // 主執行緒忙碌時瀏覽器會把多個 pointermove 合併成一個，
      // getCoalescedEvents 把中間點要回來，快速拖曳才不會斷線變折線。
      const coalesced = event.getCoalescedEvents
        ? event.getCoalescedEvents()
        : [];

      if (coalesced.length > 0) {
        for (const coalescedEvent of coalesced) {
          stroke.points.push(toCanvasPoint(coalescedEvent));
        }
      } else {
        stroke.points.push(toCanvasPoint(event));
      }

      scheduleRender();
    };

    const handlePointerUp = event => {
      const stroke = activeStrokeRef.current;
      if (!stroke || stroke.pointerId !== event.pointerId) return;
      // 畫到一半按右鍵或中鍵再放開也會送出 pointerup，但左鍵還按著，不能算收筆。
      // pointercancel / lostpointercapture 不帶有效 button，照常收筆。
      if (event.type === 'pointerup' && event.button !== 0) return;

      stroke.releasedAt = performance.now();
      activeStrokeRef.current = null;

      try {
        canvasEl.releasePointerCapture(event.pointerId);
      } catch (e) {
        /* 已經釋放過就忽略 */
      }

      scheduleRender();
    };

    // 保險：指標在畫布外放開、或視窗失焦導致收不到 pointerup 時，
    // 仍要讓這筆開始淡出，否則它會停在全不透明、rAF 也停不下來。
    const finalizeActiveStroke = event => {
      const stroke = activeStrokeRef.current;
      if (!stroke) return;
      // 非主要按鍵放開會冒泡到 window，同樣不能截斷筆畫；blur 沒有 button，一律收筆
      if (event && event.type === 'pointerup' && event.button !== 0) return;
      stroke.releasedAt = performance.now();
      activeStrokeRef.current = null;
      scheduleRender();
    };

    // 擋掉 iOS WKWebView 的長按選單與系統手勢，比照 FabricCanvas 的處理
    const preventDefault = event => {
      if (isLaserModeRef.current) event.preventDefault();
    };

    canvasEl.addEventListener('pointerdown', handlePointerDown);
    canvasEl.addEventListener('pointermove', handlePointerMove);
    canvasEl.addEventListener('pointerup', handlePointerUp);
    canvasEl.addEventListener('pointercancel', handlePointerUp);
    canvasEl.addEventListener('lostpointercapture', handlePointerUp);
    canvasEl.addEventListener('contextmenu', preventDefault);
    canvasEl.addEventListener('gesturestart', preventDefault);
    canvasEl.addEventListener('gesturechange', preventDefault);
    window.addEventListener('pointerup', finalizeActiveStroke);
    window.addEventListener('blur', finalizeActiveStroke);

    return () => {
      canvasEl.removeEventListener('pointerdown', handlePointerDown);
      canvasEl.removeEventListener('pointermove', handlePointerMove);
      canvasEl.removeEventListener('pointerup', handlePointerUp);
      canvasEl.removeEventListener('pointercancel', handlePointerUp);
      canvasEl.removeEventListener('lostpointercapture', handlePointerUp);
      canvasEl.removeEventListener('contextmenu', preventDefault);
      canvasEl.removeEventListener('gesturestart', preventDefault);
      canvasEl.removeEventListener('gesturechange', preventDefault);
      window.removeEventListener('pointerup', finalizeActiveStroke);
      window.removeEventListener('blur', finalizeActiveStroke);
    };
  }, [scheduleRender]);

  return (
    <div
      ref={wrapperRef}
      className={classnames(
        styles.laserCanvas,
        // 沿用偏好設定「畫筆點擊按鈕」：開啟時壓在互動物件之下讓按鈕／蓋片仍可點，
        // 關閉時比照 fabric 一起蓋到最上層（BookContainer 的 .covered 是同一個條件）
        { [styles.covered]: !objectPointerEventStatus },
        className
      )}
      style={{ display: isLaserMode ? 'block' : 'none' }}
    >
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{ width: `${width}px`, height: `${height}px` }}
      />
    </div>
  );
};

export default LaserCanvas;
